import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DetailedPoliceReport,
  PoliceReportAttestationStatus,
} from '@/services/api/generated/policeReportsService';
import { enhancedApi as investigationRequestsApi } from '@/services/api/investigationRecordsRequestsService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { enhancedPoliceReportsApi } from '@/services/api/policeReportsService';

import { BrowserRunInspectorModal } from './browser-run-inspector';
import { CallButton } from './CallButton';
import { GenerateFoiaLetterModal } from './GenerateFoiaLetterModal';
import { PoliceStatusBadge } from './PoliceStatusBadge';
import { SendMailModal } from './QuickSendMailModal';
import { SentMailIndicators } from './SentMailIndicators';
import { StatusControls } from './StatusControls';

// Helper to extract short domain from URL
const extractDomain = (url: string): string => {
  try {
    const { hostname } = new URL(url);
    // Remove www. prefix if present
    return hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const BrowserAutomationBadge = ({
  policeReportId,
  matterId,
  departmentName,
  vendorLabel,
  portalUrl,
}: {
  policeReportId: string;
  matterId: string;
  departmentName: string;
  vendorLabel: string;
  portalUrl?: string;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  const [startBrowserAgent, { isLoading: isFiring }] =
    enhancedPoliceReportsApi.usePoliceReportsStartBrowserAgentMutation();
  const {
    data: runsData,
    isLoading: isFetching,
    refetch,
  } = enhancedPoliceReportsApi.usePoliceReportsGetBrowserRunsQuery(
    { matterId, id: policeReportId },
    { skip: !showInspector },
  );

  const runs = runsData?.runs || [];
  const loading = isFiring || isFetching;

  const handleFireRun = async () => {
    try {
      const result = await startBrowserAgent({
        matterId,
        id: policeReportId,
      }).unwrap();

      toast.success('Browser automation started', {
        description: result.message || 'Run started',
      });

      setShowMenu(false);

      if (showInspector) {
        await refetch();
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'error' in error
          ? String(error.error)
          : 'Failed to start automation';
      toast.error(errorMessage);
    }
  };

  const handleViewRuns = () => {
    setShowMenu(false);
    setShowInspector(true);
  };

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowMenu(!showMenu)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded"
          disabled={loading}
        >
          <GlobeAltIcon className="h-3.5 w-3.5" />
          {vendorLabel}
          <ChevronDownIcon className="w-3 h-3" />
        </button>
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowMenu(false);
              }}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
            <div className="absolute left-0 top-full mt-1 bg-white rounded border border-gray-200 shadow-lg z-20 min-w-[160px]">
              <button
                type="button"
                onClick={handleFireRun}
                disabled={loading}
                className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50"
              >
                Launch Portal Check
              </button>
              <button
                type="button"
                onClick={handleViewRuns}
                disabled={loading}
                className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t border-gray-100"
              >
                View History
              </button>
              {portalUrl && (
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t border-gray-100 block"
                  onClick={() => setShowMenu(false)}
                >
                  Open Portal Website
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {showInspector && (
        <BrowserRunInspectorModal
          isOpen={showInspector}
          onClose={() => setShowInspector(false)}
          runs={runs}
          departmentName={departmentName}
        />
      )}
    </>
  );
};

interface PoliceReportCardProps {
  report: DetailedPoliceReport;
  matterId: string;
  onSelect: () => void;
  firmRequiresFoia?: boolean;
}

export const PoliceReportCard = ({
  report,
  matterId,
  onSelect,
  firmRequiresFoia,
}: PoliceReportCardProps) => {
  const [showMailMenu, setShowMailMenu] = useState(false);
  const [showFormsMenu, setShowFormsMenu] = useState(false);
  const [modalType, setModalType] = useState<'cheque' | 'foia' | null>(null);
  const [showGenerateFoiaModal, setShowGenerateFoiaModal] = useState(false);

  const { data: matter } = useGetMatterQuery(matterId, { skip: !matterId });

  const handleCopyReportNumber = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (report.report_identifier) {
      navigator.clipboard.writeText(report.report_identifier);
      toast.success('Report number copied to clipboard');
    }
  };

  const [sendCheque, { isLoading: isSendingCheque }] =
    investigationRequestsApi.useInvestigationRecordsRequestsSendChequeMutation();
  const [sendFoia, { isLoading: isSendingFoia }] =
    investigationRequestsApi.useInvestigationRecordsRequestsSendFoiaLetterMutation();

  const department = report.police_department;
  const isRejected = report.attestation_status === 'rejected_verified';

  // Parse channels by type
  const channels = useMemo(() => {
    const records = department.records_channels || [];
    return {
      call: records.filter((c) => c.channel_type === 'phone'),
      mail: records.filter((c) => c.channel_type === 'mail'),
      portals: records.filter((c) => c.channel_type === 'online_portal'),
    };
  }, [department.records_channels]);

  // Eligibility for automated mail actions - use data from DetailedPoliceReport
  const hasCheques = (report.sent_cheques?.length ?? 0) > 0;
  const hasLetters = (report.sent_letters?.length ?? 0) > 0;

  const showCheque = !hasCheques;
  const showFoia = firmRequiresFoia && !hasLetters;

  const handleConfirmSend = async (
    channelId?: string,
    templateId?: string,
    overrides?: Record<string, string>,
  ) => {
    if (!channelId) {
      toast.error('No valid mail channel selected');
      return;
    }

    try {
      if (modalType === 'cheque') {
        await sendCheque({
          matterId,
          sendChequeRequest: {
            police_report_id: report.id,
            channel_id: channelId,
          },
        }).unwrap();
        toast.success('Cheque workflow started');
      } else if (modalType === 'foia') {
        await sendFoia({
          matterId,
          sendFoiaLetterRequest: {
            police_report_id: report.id,
            channel_id: channelId,
            template_id: templateId,
            overrides,
          },
        }).unwrap();
        toast.success('FOIA letter workflow started');
      }
      setModalType(null);
      setShowMailMenu(false);
      // No need to reload - RTK Query will automatically refetch the data
    } catch (error) {
      const err = error as { data?: { error?: string; reason?: string } };
      toast.error(err?.data?.error || 'Failed to send mail');
      setModalType(null);
    }
  };

  return (
    <div
      className={`border border-gray-200 rounded-lg bg-white p-4 md:p-5 space-y-3 ${isRejected ? 'opacity-60' : ''}`}
    >
      {/* Header: Dept Name | State - Status */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={onSelect}
          className="text-left group min-w-0 flex-shrink"
        >
          <div className="flex items-baseline gap-2">
            <span
              className={`text-sm font-medium group-hover:underline ${isRejected ? 'text-gray-500 line-through' : 'text-gray-900'}`}
            >
              {department.name || 'Unknown'}
            </span>
            {department.state_code && (
              <span className="text-xs text-gray-500">
                {department.state_code}
              </span>
            )}
          </div>
          {report.report_identifier && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleCopyReportNumber}
                  className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 mt-2 px-2 py-1 -ml-2 rounded group/copy transition-colors"
                >
                  <span className="font-medium">
                    Report #{report.report_identifier}
                  </span>
                  <ClipboardDocumentIcon className="h-3.5 w-3.5 opacity-0 group-hover/copy:opacity-100 transition-opacity" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Click to copy report number</TooltipContent>
            </Tooltip>
          )}
        </button>

        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <PoliceStatusBadge
            status={report.attestation_status as PoliceReportAttestationStatus}
            showFullLabel={false}
            size="sm"
            showIcon={false}
          />
          <SentMailIndicators
            letters={report.sent_letters}
            cheques={report.sent_cheques}
          />
          {report.attestation_status && (
            <StatusControls
              reportId={report.id}
              matterId={matterId}
              currentStatus={report.attestation_status}
              departmentName={department.name || 'Unknown Department'}
            />
          )}
        </div>
      </div>

      {/* Description - attestation notes */}
      {report.attestation_description && (
        <div className="text-xs text-gray-600 leading-relaxed">
          {report.attestation_description}
        </div>
      )}

      {/* Action bar - consolidated */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
        {/* Mail dropdown - automated requests */}
        {!isRejected &&
          channels.mail.length > 0 &&
          (showCheque || showFoia) && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMailMenu(!showMailMenu)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-purple-700 bg-purple-50 hover:bg-purple-100 rounded whitespace-nowrap"
              >
                <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                Mail
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
              </button>
              {showMailMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMailMenu(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setShowMailMenu(false);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Close menu"
                  />
                  <div className="absolute left-0 top-full mt-1 bg-white rounded border border-gray-200 shadow-lg z-20 min-w-[160px]">
                    {showCheque && (
                      <button
                        type="button"
                        onClick={() => {
                          setModalType('cheque');
                          setShowMailMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50"
                      >
                        Request Police Report
                      </button>
                    )}
                    {showFoia && (
                      <button
                        type="button"
                        onClick={() => {
                          setModalType('foia');
                          setShowMailMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t border-gray-100"
                      >
                        FOIA Request
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

        {/* Call */}
        {!isRejected && (
          <CallButton
            phoneChannels={channels.call}
            departmentName={department.name || 'Department'}
            matterId={matterId}
          />
        )}

        {/* Mail (manual - when no automated options) */}
        {!isRejected &&
          channels.mail.length > 0 &&
          !(showCheque || showFoia) && (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 whitespace-nowrap"
            >
              <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0" />
              Mail
            </button>
          )}

        {/* Forms dropdown */}
        {!isRejected && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFormsMenu(!showFormsMenu)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-orange-700 bg-orange-50 hover:bg-orange-100 rounded whitespace-nowrap"
            >
              <DocumentTextIcon className="w-3.5 h-3.5 flex-shrink-0" />
              Forms
              <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
            </button>
            {showFormsMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowFormsMenu(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowFormsMenu(false);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Close menu"
                />
                <div className="absolute left-0 top-full mt-1 bg-white rounded border border-gray-200 shadow-lg z-20 min-w-[160px]">
                  <button
                    type="button"
                    onClick={() => {
                      setShowGenerateFoiaModal(true);
                      setShowFormsMenu(false);
                    }}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50"
                  >
                    Download FOIA Letter
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Automated portals - browser automation badge */}
        {!isRejected &&
          (() => {
            const automatedPortal = channels.portals.find(
              (p) =>
                p.major_vendor === 'lexis_nexis_buycrash' ||
                p.major_vendor === 'txdot_cris',
            );
            if (!automatedPortal) return null;

            const vendorLabel =
              automatedPortal.major_vendor === 'lexis_nexis_buycrash'
                ? 'BuyCrash'
                : 'CRIS';
            const portalUrl =
              automatedPortal.portal_url ||
              automatedPortal.handle?.value ||
              undefined;

            return (
              <BrowserAutomationBadge
                policeReportId={report.id}
                matterId={matterId}
                departmentName={department.name || 'Unknown'}
                vendorLabel={vendorLabel}
                portalUrl={portalUrl}
              />
            );
          })()}

        {/* Regular portals - links with domain */}
        {!isRejected &&
          channels.portals
            .filter(
              (p) =>
                p.major_vendor !== 'lexis_nexis_buycrash' &&
                p.major_vendor !== 'txdot_cris',
            )
            .map((portal, idx) => {
              const url = portal.portal_url || portal.handle?.value || '#';
              const domain = extractDomain(url);
              return (
                <a
                  key={idx}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap"
                  title={portal.title || portal.description || undefined}
                >
                  <GlobeAltIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  {domain}
                </a>
              );
            })}
      </div>

      {/* Unified send mail modal */}
      {modalType && (
        <SendMailModal
          isOpen={!!modalType}
          onClose={() => setModalType(null)}
          onConfirm={handleConfirmSend}
          type={modalType}
          isLoading={isSendingCheque || isSendingFoia}
          departmentId={department.id}
          departmentName={department.name || 'Unknown'}
          policeReportId={report.id}
          matterId={matterId}
          firmId={matter?.firm?.id}
        />
      )}

      {/* Generate FOIA Letter Modal */}
      {showGenerateFoiaModal && (
        <GenerateFoiaLetterModal
          isOpen={showGenerateFoiaModal}
          onClose={() => setShowGenerateFoiaModal(false)}
          reportId={report.id}
          matterId={matterId}
          departmentName={department.name || 'Unknown Department'}
        />
      )}
    </div>
  );
};
